import { LogOut, Save, ShieldCheck, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, APIError } from "../api";
import { useAuth } from "../auth";
import { PageState } from "../components/Media";
import type { User, Visibility } from "../types";

export function ProfilePage() {
  const { user, openSignIn, refreshUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [bio, setBio] = useState("");
  const [visibility, setVisibility] = useState<Visibility>("public");
  const [counts, setCounts] = useState({ library: 0, lists: 0, friends: 0 });
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isSaving, setSaving] = useState(false);
  const [isDeleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!user) return;
    setDisplayName(user.displayName); setUsername(user.username); setBio(user.bio); setVisibility(user.profileVisibility);
    api<{ user: User; counts: typeof counts }>("/v1/me").then((response) => setCounts(response.counts)).catch(() => undefined);
  }, [user]);

  if (!user) return <PageState title="Make My K-List yours" message="Sign in to choose a handle, personalize your profile, and control what people can see." action={<button className="primary-button" onClick={openSignIn}>Sign in</button>} />;

  const save = async (event: React.FormEvent) => {
    event.preventDefault(); setError(null); setMessage(null); setSuggestions([]);
    setSaving(true);
    try {
      await api("/v1/me", { method: "PATCH", body: JSON.stringify({ displayName, username, bio, profileVisibility: visibility }) });
      await refreshUser(); setMessage("Profile saved.");
    } catch (saveError) {
      if (saveError instanceof APIError) { setError(saveError.message); setSuggestions(saveError.suggestions); }
      else setError("Profile could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    const confirmed = window.confirm("Permanently delete your My K-List account, profile, synced library, lists, and friendships? This cannot be undone.");
    if (!confirmed) return;
    setDeleting(true); setError(null);
    try {
      await api("/v1/me", { method: "DELETE" });
      await signOut().catch(() => undefined);
      navigate("/");
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Account deletion failed.");
      setDeleting(false);
    }
  };

  return <div className="standard-page profile-page">
    <header className="profile-hero"><span className="avatar oversized">{user.displayName.slice(0, 1).toUpperCase()}</span><div><p className="eyebrow">내 프로필 · My profile</p><h1>{user.displayName}</h1><p>@{user.username}</p></div><div className="profile-counts"><span><strong>{counts.library}</strong>titles</span><span><strong>{counts.lists}</strong>lists</span><span><strong>{counts.friends}</strong>friends</span></div></header>
    <form className="profile-form" onSubmit={(event) => void save(event)}><div className="form-heading"><div><h2>Profile details</h2><p>Your display name can be anything. Your handle is unique.</p></div><ShieldCheck size={22} /></div>
      <div className="field-grid"><label>Display name<input name="displayName" autoComplete="name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={80} required /></label><label>Username<div className="input-prefix"><span>@</span><input name="username" autoComplete="username" autoCapitalize="none" spellCheck={false} value={username} onChange={(event) => setUsername(event.target.value.toLowerCase())} minLength={4} maxLength={24} required /></div><small>4–24 lowercase letters, numbers, periods, or underscores.</small></label></div>
      <label>Bio<textarea name="bio" autoComplete="off" value={bio} onChange={(event) => setBio(event.target.value)} maxLength={280} placeholder="A little about your taste…" /><small>{bio.length}/280</small></label>
      <label>Profile visibility<select name="profileVisibility" value={visibility} onChange={(event) => setVisibility(event.target.value as Visibility)}><option value="public">Public</option><option value="friends">Friends only</option><option value="private">Private</option></select></label>
      {suggestions.length > 0 && <div className="suggestions"><span>Try:</span>{suggestions.map((suggestion) => <button type="button" key={suggestion} onClick={() => setUsername(suggestion)}>@{suggestion}</button>)}</div>}
      {error && <p className="form-error" role="alert">{error}</p>}{message && <p className="form-success" role="status" aria-live="polite">{message}</p>}
      <div className="form-actions"><button className="primary-button" type="submit" disabled={isSaving}><Save size={16} />{isSaving ? "Saving…" : "Save changes"}</button><button type="button" className="danger-button" onClick={async () => { await signOut(); navigate("/"); }}><LogOut size={16} />Sign out</button><button type="button" className="danger-button" disabled={isDeleting} onClick={() => void deleteAccount()}><Trash2 size={16} />{isDeleting ? "Deleting…" : "Delete account"}</button></div>
    </form>
  </div>;
}
